import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import KonfhubContainer from "../../../revampComponents/Atom/Container";
import AddNEditTask from "./AddNEditTask";
import { addCategories, addExhibitors, buildTaskPayload, cleanPayload, convertToEventTimeZone, convertToUTC, LABELS, removeCategory, removeExhibitor, syncAllWithDefaults, updateCategoryDeadline, updateCategoryDeadlines, updateExhibitorDeadline, updateExhibitorDeadlines, validationSchema } from "./TaskManagementConstant";
import { diffAndCommon } from "../../../services/util";
import { createTask, updateTask, getAllTasks } from "../../../revampCRUD/taskManagement.crud";
import customToast from "../../../components/common/basic/Toast";
import { useFormik } from "formik";
import { ErrMessages, CrudMessages } from "../../EventInfo/config/error-constants";
import LoadingSpinner from "../../../revampComponents/Atom/Loading";
import { getUTCDateNTime } from "../../../Utils/helpers";
import { removeEmptyValues } from "../../../reusable/common";
const AddNEditInterface = (props) => {
  const { eventId, taskId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const entityCategoryId = location.state?.entityCategoryId;
  const exhibitorId = location.state?.exhibitorId;
  const [isEdit, setIsEdit] = useState(!!taskId);
  const [edit, setEdit] = useState(false)
  const [loading, setLoading] = useState(false);
  const [editCategorIds, setEditCategoryIds] = useState([]);
  const [isExhibitorToggle, setIsExhibitorToggle] = useState(false);
  const [taskDetails, setTaskDetails] = useState({
    task_type: "",
    title: "",
    description: "",
    priority: "",
    default_due_date: "",
    files: [],
    default_reminder_date: "",
    category_ids: [],
    exhibitor_ids: [],
    task_category_id: '',
    exhibitors: {
      items: [],  // Current selected exhibitors with deadlines
      _original: []  // Original data from API (for edit mode)
    },
    categories: {
      items: [],  // Current selected categories with deadlines
      _original: []  // Original data from API (for edit mode)
    },
    auto_assign_type: null
  });

  const formik = useFormik({
    initialValues: taskDetails,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const updated_details = diffAndCommon("diff", [values, formik.initialValues], []);
      const cleanedDetails = cleanPayload(updated_details);
      delete cleanedDetails.category_ids;
      delete cleanedDetails.exhibitor_ids;

      const payload = removeEmptyValues(buildTaskPayload(values, isEdit, props.eventInfo?.time_zone));

      try {
        let response;
        if (isEdit) {
          response = await updateTask(taskId, payload);
        } else {
          response = await createTask(payload);
        }

        if (response.data) {
          customToast.success(isEdit ? CrudMessages.UPDATE_MESSAGE : CrudMessages.ADDED_MESSAGE);
          formik.resetForm();
          history.push(exhibitorId
            ? `/e/${eventId}/exhibitor-portal/${exhibitorId}/tasks`
            : `/e/${eventId}/exhibitor-portal/task-management`)
        } else {
          customToast.error(ErrMessages.DEFAULT_ERROR_MESSAGE);
        }
      } catch (err) {
        customToast.error(ErrMessages.INTERNAL_SERVER_ERROR);
      } finally {
        setLoading(false);
      }
    },
  });

  const memoizedTaskDetails = useMemo(
    () => JSON.parse(JSON.stringify(taskDetails)),
    [JSON.stringify(taskDetails)]
  );
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      formik.resetForm({ values: memoizedTaskDetails });
    }
  }, [memoizedTaskDetails, isEdit]);



  useEffect(() => {
    if (!taskId) return;

    setLoading(true);
    setIsEdit(true);

    const fetchTaskDetails = async () => {
      try {
        const response = await getAllTasks();
        const tasks = response?.data?.tasks || [];
        const task = tasks?.find((t) => t?.task_id === taskId);

        if (task) {

          const {
            exhibitors,
            category_ids,
            default_due_date,
            default_reminder_date,
            ...otherDetails
          } = task;

          const convertedDueDate = convertToEventTimeZone(default_due_date, props?.eventInfo);
          const convertedReminderDate = convertToEventTimeZone(default_reminder_date, props?.eventInfo);

          // Map exhibitors - INCLUDE category_id!
          const exhibitorItems = (exhibitors || []).map(e => ({
            id: e.id,
            entity_name: e?.entity_name,
            ...(category_ids?.length > 0 ? { category_id: e?.category_id } : {}),
            due_date: e?.due_date ? convertToEventTimeZone(e?.due_date, props?.eventInfo) : null,
            reminder_date: e?.reminder_date ? convertToEventTimeZone(e?.reminder_date, props?.eventInfo) : null
          }));

          formik.setValues({
            ...otherDetails,
            default_due_date: convertedDueDate,
            default_reminder_date: convertedReminderDate,
            category_ids: category_ids || [],
            exhibitors: {
              items: exhibitorItems,
              _original: JSON.parse(JSON.stringify(exhibitorItems))
            },
            categories: {
              items: [],
              _original: []
            }
          });

          setEditCategoryIds(category_ids || []);

          if (category_ids && category_ids?.length > 0) {
            setIsExhibitorToggle(false);
          } else if (exhibitors && exhibitors?.length > 0) {
            setIsExhibitorToggle(true);
          }

          setLoading(false);
        }
      } catch (error) {
        customToast.error("Error fetching task details");
        setLoading(false);
      }
    };

    fetchTaskDetails();
  }, [taskId]);


  useEffect(() => {
    if (exhibitorId && !isEdit) {
      const updatedTaskDetails = {
        ...taskDetails,
        exhibitor_ids: [exhibitorId],
        category_ids: [],
      };

      setTaskDetails(updatedTaskDetails);
      if (!formik.values.exhibitor_ids.length) {
        formik.setValues(updatedTaskDetails);
      }
    }
  }, [exhibitorId, isEdit]);


  useEffect(() => {
    if (location.state?.isPredefinedTask && location.state?.predefinedTaskDetails) {
      const { module_name, task_type, task_column_mapping } = location.state.predefinedTaskDetails;

      if (formik.values.title !== module_name) {
        formik.setFieldValue("title", module_name);
      }
      if (formik.values.task_type !== task_type) {
        formik.setFieldValue("task_type", task_type);
      }
      if (formik.values.task_column_mapping !== task_column_mapping) {
        formik.setFieldValue("task_column_mapping", task_column_mapping);
      }
    }
  }, [location.state?.isPredefinedTask, location.state?.predefinedTaskDetails, formik.values]);
  
  return (
    <>
      {loading ? <LoadingSpinner /> : <KonfhubContainer>
        <h4 className="px-4 pt-2 task-edit-add-title">
          {isEdit ? LABELS.EDIT_TASK : LABELS.CREATE_TASK}
        </h4>
        <AddNEditTask
          formik={formik}
          setTaskDetails={setTaskDetails}
          predefinedTaskDetails={location.state?.predefinedTaskDetails || {}}
          isPredefinedTask={location.state?.isPredefinedTask || false}
          editCategorIds={editCategorIds}
          setEditCategoryIds={setEditCategoryIds}
          isEdit={isEdit}
          entityCategoryId={entityCategoryId}
          exhibitorId={exhibitorId}
          taskDetails={taskDetails}
          edit={edit}
          setEdit={setEdit}
          eventDetails={props.eventInfo}
          setIsExhibitorToggle={setIsExhibitorToggle}
          isExhibitorToggle={isExhibitorToggle}
          addExhibitors={(exhibitors) => addExhibitors(formik, exhibitors)}
          addCategories={(categories) => addCategories(formik, categories)}
          removeExhibitor={(id) => removeExhibitor(formik, id)}
          removeCategory={(id) => removeCategory(formik, id)}
          updateExhibitorDeadline={(id, field, value) => updateExhibitorDeadline(formik, id, field, value)}
          updateCategoryDeadline={(id, field, value) => updateCategoryDeadline(formik, id, field, value)}
          updateExhibitorDeadlines={(id, field, value) => updateExhibitorDeadlines(formik, id, field, value)}
          updateCategoryDeadlines={(id, field, value) => updateCategoryDeadlines(formik, id, field, value)}
          syncAllWithDefaults={(type, dateType, newValue) => syncAllWithDefaults(formik, type, dateType, newValue)}
        />
      </KonfhubContainer>}
      <style>
        {`
          .task-edit-add-title {
            color: var(--primaryColor) !important;
            font-weight: 600;
            font-size: 18px;
          }
        `}
      </style>
    </>
  );
};

export default AddNEditInterface;
